import React, { useCallback } from 'react'
import { Box, IconButton, Paper, Skeleton, Stack, Theme, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'

import FlagIcon from '@mui/icons-material/Flag'
import GroupsIcon from '@mui/icons-material/Group'
import FavoriteIcon from '@mui/icons-material/Favorite'
import EyeIcon from '@mui/icons-material/RemoveRedEye'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import AppsIcon from '@mui/icons-material/Apps'
import ViewCompactIcon from '@mui/icons-material/ViewCompact'

import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import NoPageConnectedComponent from '@professocial/professocialFormComponents/FormComponents/NoPageConnectedComponent'
import { NetworkPage } from '@professocial/professocialTypes/types/commonTypes'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import getDateFormat from '../../../../defaults/getDateFormat'
import { NetworkType } from '@3m5/sm_publish-frontend-api'
import { useDispatch, useSelector } from 'react-redux'
import { getInstagramNumberOfPosts } from '../../../../store/common/common.selectors'
import { setInstagramNumberOfPost } from '../../../../store/common/common.actions'

interface IProps {
  networkPage: NetworkPage
  activeTab: NetworkType
}

const useStyles = makeStyles(() => ({
  item: {
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    color: 'var(--customMainColor)',
  },
}))

const DashboardNetworkContentProperties: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const instagramNumberOfPosts = useSelector(getInstagramNumberOfPosts)
  const dispatchSetInstagramNumberOfPosts = useCallback((params: 9 | 24) => dispatch(setInstagramNumberOfPost(params)), [dispatch])
  const storedInstagramPageId: string | null = window.localStorage.getItem('instagramPageNetworkId')
  const storedFacebookPageId: string | null = window.localStorage.getItem('facebookPageNetworkId')

  return (
    <>
      {(() => {
        switch (props.activeTab) {
        case NetworkType.Facebook:
          return (
            <>
              <Paper sx={{ px: 4, py: 2, mb: 1 }}>
                {storedFacebookPageId
                  ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 'auto' }}
                      alignItems={{ xs: 'center' }}>
                      <Stack
                        direction="row"
                        spacing={{ xs: 2, md: 4 }}
                        justifyContent={{ xs: 'center', sm: 'flex-start' }}
                        flexWrap="wrap"
                      >
                        <div className={classes.item}>
                          {props.networkPage.facebookPage
                            ? <>
                              <FlagIcon/><Typography>{t('coreDashboard:activeSince')} {formatDate(props.networkPage.facebookPage.firstConnect.getTime(), getDateFormat('date'))}</Typography></>
                            : <Skeleton animation="wave" height={24} width={116}/>}
                        </div>
                        <div className={classes.item}>
                          {props.networkPage.facebookPage
                            ? <>
                              <GroupsIcon/><Typography>{props.networkPage.facebookPage?.pageStatistics?.fanCount} {t('coreDashboard:fans')}</Typography></>
                            : <Skeleton animation="wave" height={24} width={90}/>}
                        </div>
                        <div className={classes.item}>
                          {props.networkPage.facebookPage
                            ? <>
                              <Typography sx={{ fontSize: '2.3rem', lineHeight: 0 }}>⌀</Typography>
                              <Typography>{t('coreDashboard:coverage')} {props.networkPage.facebookPage?.pageStatistics?.weeklyTotalReach} {props.networkPage.facebookPage?.pageStatistics?.weeklyTotalReach === 1 ? t('coreDashboard:person') : t('coreDashboard:persons')}</Typography></>
                            : <Skeleton animation="wave" height={24} width={171}/>}
                        </div>
                      </Stack>
                      <Box marginLeft={{ sm: 'auto' }}>
                        <ButtonComponent
                          color="primary"
                          text={t('coreDashboard:goToFacebookFanSite')}
                          link={`https://www.facebook.com/${props.networkPage.facebookPage?.networkPageId}`}
                        />
                      </Box>
                    </Stack>
                  )
                  : <NoPageConnectedComponent link="/user/settings/connect?startTab=facebook"/>}
              </Paper>
            </>
          )
        case NetworkType.Instagram:
          return (
            <Paper sx={{ px: 4, py: 2, mb: 1 }}>
              {storedInstagramPageId
                ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 'auto' }}
                    alignItems={{ xs: 'center' }}>
                    <Stack
                      direction="row"
                      spacing={{ xs: 2, md: 4 }}
                      justifyContent={{ xs: 'center', sm: 'flex-start' }}
                      flexWrap="wrap"
                    >
                      <div className={classes.item}>
                        {props.networkPage.instagramPage && props.networkPage.instagramPage.firstConnect
                          ? <><FlagIcon/>
                            <Typography>{t('coreDashboard:activeSince')} {formatDate(props.networkPage.instagramPage.firstConnect.getTime(), getDateFormat('date'))}</Typography></>
                          : <Skeleton animation="wave" height={24} width={116}/>}
                      </div>
                      <div className={classes.item}>
                        {props.networkPage.instagramPage
                          ? <>
                            <QuestionAnswerIcon/><Typography>{props.networkPage.instagramPage.pageStatistics?.mediaCount} {props.networkPage.instagramPage.pageStatistics?.mediaCount === 1 ? t('coreDashboard:instagramPost') : t('coreDashboard:instagramPosts')}</Typography></>
                          : <Skeleton animation="wave" height={24} width={116}/>}
                      </div>
                      <div className={classes.item}>
                        {props.networkPage.instagramPage
                          ? <><GroupsIcon/>
                            <Typography>{props.networkPage.instagramPage.pageStatistics?.followersCount} {t('coreDashboard:follower')}</Typography></>
                          : <Skeleton animation="wave" height={24} width={116}/>}
                      </div>
                      <div className={classes.item}>
                        {props.networkPage.instagramPage
                          ? <><FavoriteIcon/>
                            <Typography>{props.networkPage.instagramPage.pageStatistics?.followsCount} {t('coreDashboard:subscribed')}</Typography></>
                          : <Skeleton animation="wave" height={24} width={116}/>}
                      </div>
                    </Stack>
                    <Box>
                      <IconButton
                        color="info"
                        disabled={instagramNumberOfPosts === 9}
                        sx={{ px: 1, py: 0 }}
                        onClick={() => dispatchSetInstagramNumberOfPosts(9)}>
                        <AppsIcon/>
                      </IconButton>
                      <IconButton
                        color="info"
                        sx={{ '& .MuiSvgIcon-root': { fontSize: '2.11rem' }, px: 1, py: 0 }}
                        disabled={instagramNumberOfPosts === 24}
                        onClick={() => dispatchSetInstagramNumberOfPosts(24)}
                      >
                        <ViewCompactIcon/>
                      </IconButton>
                    </Box>
                    <Box marginLeft={{ sm: 'auto' }}>
                      <ButtonComponent
                        link={`https://www.instagram.com/${props.networkPage.instagramPage?.pageName}`}
                        text={t('coreDashboard:goToInstagramSite')}
                      />
                    </Box>
                  </Stack>
                )
                : <NoPageConnectedComponent link="/user/settings/connect?startTab=instagram"/>}
            </Paper>
          )
        case NetworkType.LinkedIn:
          return (
            <Paper sx={{ px: 4, py: 2, mb: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 'auto' }}
                alignItems={{ xs: 'center' }}>
                <Stack
                  direction="row"
                  spacing={{ xs: 2, md: 4 }}
                  justifyContent={{ xs: 'center', sm: 'flex-start' }}
                  flexWrap="wrap"
                >
                  <div className={classes.item}>
                    {props.networkPage.linkedInPage
                      ? <><GroupsIcon/><Typography>{props.networkPage.linkedInPage?.pageStatistics?.shareCount} {t('coreDashboard:follower')}</Typography></>
                      : <Skeleton animation="wave" height={24} width={90}/>}
                  </div>
                  <div className={classes.item}>
                    {props.networkPage.linkedInPage
                      ? <><EyeIcon/><Typography>{t('coreDashboard:impressions')}: {props.networkPage.linkedInPage?.pageStatistics?.uniqueImpressionsCount} {props.networkPage.linkedInPage?.pageStatistics?.uniqueImpressionsCount === 1 ? t('coreDashboard:person') : t('coreDashboard:persons')}</Typography></>
                      : <Skeleton animation="wave" height={24} width={171}/>}
                  </div>
                  <div className={classes.item}>
                    {props.networkPage.linkedInPage
                      ? <><QuestionAnswerIcon/><Typography>{props.networkPage.linkedInPage?.pageStatistics?.commentCount} {props.networkPage.linkedInPage?.pageStatistics?.commentCount === 1 ? t('coreDashboard:comment') : t('coreDashboard:comments')}</Typography></>
                      : <Skeleton animation="wave" height={24} width={171}/>}
                  </div>
                  <div className={classes.item}>
                    {props.networkPage.linkedInPage
                      ? <><FavoriteIcon/><Typography>{props.networkPage.linkedInPage.pageStatistics?.likeCount} {props.networkPage.linkedInPage?.pageStatistics?.likeCount === 1 ? 'Like' : 'Likes'}</Typography></>
                      : <Skeleton animation="wave" height={24} width={116}/>}
                  </div>
                </Stack>
                <Box marginLeft={{ sm: 'auto' }}>
                  <ButtonComponent
                    color="primary"
                    text={t('coreDashboard:goToLinkedInSite')}
                    link={'https://www.linkedin.com/company/102250856/admin/feed/posts/'}
                  />
                </Box>
              </Stack>
            </Paper>
          )
        }
      })()}
    </>
  )
}
export default DashboardNetworkContentProperties
